/**
 * Every exported slide carries the same stylesheets (a Google Fonts <link> and
 * a small <style>). Re-entering the document on each bite change, they made
 * Safari rebuild styles and fonts (~2.6s on an iPad); in <head>, only once.
 */

const SLIDE_STYLESHEET_RE =
  /<link\b[^>]*rel=["']?stylesheet["']?[^>]*>|<style\b[^>]*>[\s\S]*?<\/style>/gi;

export const HOISTED_ATTRIBUTE = "data-hoisted-slide-stylesheet";

// Keys of the stylesheets already in <head>: a link's href, a style's text.
const hoistedKeys = new Set<string>();

function parseTag(tag: string): HTMLLinkElement | HTMLStyleElement | null {
  const template = document.createElement("template");
  template.innerHTML = tag;
  const element = template.content.firstElementChild;
  return element instanceof HTMLLinkElement ||
    element instanceof HTMLStyleElement
    ? element
    : null;
}

function hoistTag(tag: string) {
  const element = parseTag(tag);
  if (!element) return;
  const key =
    element instanceof HTMLLinkElement
      ? `link:${element.href}`
      : `style:${element.textContent ?? ""}`;
  if (hoistedKeys.has(key)) return;
  hoistedKeys.add(key);
  element.setAttribute(HOISTED_ATTRIBUTE, "");
  document.head.appendChild(element);
}

/**
 * Moves the slide's stylesheets into <head>, once per distinct sheet, and
 * returns the HTML without them. They stay until removeHoistedSlideStyles.
 */
export function hoistSlideStylesheets(html: string): string {
  const tags = html.match(SLIDE_STYLESHEET_RE);
  if (!tags) return html;
  tags.forEach(hoistTag);
  return html.replace(SLIDE_STYLESHEET_RE, "");
}

/**
 * Removes the hoisted <style> sheets when their lesson leaves the screen:
 * their rules lay out slides and must not outlive it. The font <link> stays;
 * it only declares fonts, and removing it makes Safari rebuild its font set.
 */
export function removeHoistedSlideStyles(): void {
  document.head
    .querySelectorAll(`style[${HOISTED_ATTRIBUTE}]`)
    .forEach((style) => style.remove());
  hoistedKeys.forEach((key) => {
    if (key.startsWith("style:")) hoistedKeys.delete(key);
  });
}
